<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :disabled="isDisabled"
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props">
        <slot></slot>
      </div>
    </template>

    <v-card v-if="noThingsSelected">
      <v-card-title class="d-flex justify-space-between">
        {{ $t('routes.node.downloadDialogNoFiles') }}

        <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="scrollable">
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="dialog = false">{{ $t('routes.node.downloadDialogOK') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-card v-else>
      <v-card-title class="d-flex justify-space-between">
        {{ $t('routes.node.downloadFilesTitle') }}

        <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div class="text-body-1 mt-2">{{ $t('routes.node.downloadDialogMessage') }}</div>

        <v-checkbox
          v-if="allowExclude && !hasSelectedAssets"
          v-model="excludeAssets"
          :label="$t('routes.node.downloadDialogExclude')"
          hide-details
        >
        </v-checkbox>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="flat" @click="downloadThings" elevation="0">{{ $t('common.download') }}
        </v-btn>
        <v-btn variant="text" @click="dialog = false">{{ $t('common.cancel') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js';
import { useCommonStore } from '@/stores/CommonStore.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import Api from '@/api/Api';

export default {
  mixins: [DialogActionKeys],
  name: "DownloadDialog",
  emits: ['menu-close', 'update:modelValue'],
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    nodes: {
      type: Array,
      required: false,
    },
    pods: {
      type: Array,
      required: false
    },
    assets: {
      type: Array,
      required: false
    },
    allowExclude: {
      type: Boolean,
      required: false,
      default: true,
    },
    isDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      excludeAssets: false,
    };
  },
  mounted() {
    this.resetForm();
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.resetForm();
      }
    }
  },
  methods: {
    resetForm() {
      this.excludeAssets = this.allowExclude ? true : false;
    },
    async downloadThings() {
      const hasPods = (this.pods && this.pods.length > 0);
      const hasNodes = (this.nodes && this.nodes.length > 0);
      const hasAssets = (this.assets && this.assets.length > 0);
      const completeState = 3;

      // download single raw file
      if (!hasNodes && hasPods && this.pods.length === 1 && this.excludeAssets) {
        const rawAsset = this?.pods[0]?.asset?.raw ? this?.pods[0]?.asset?.raw[0] : null;

        if (rawAsset?.url) {
          this.dialog = false;

          // check if asset is complete
          if (rawAsset?.state !== completeState) {
            this.$showMessage(this.$t('routes.pod.fileNotCompletedDownloadError'), { type: 'error' });
            return;
          }

          try {
            const response = await Api.pod.issueRawAssetDownloadUrl(this.pods[0].id, rawAsset.id);

            if (response?.data?.url) {
              window.location = response.data.url;
            }
          } catch (error) {
            const message = error?.response?.error
              ? this.$localizeMessage(error.response.error, this.$t('backend.system-error'))
              : this.$t('backend.system-error');
            this.$showMessage(message, { type: 'error' });
          }
          return;
        }
      }

      // download zip archive
      const payload = {
        nodeids: hasNodes ? this.nodes.map((x) => x.id) : [],
        podids: hasPods ? this.pods.map((i) => i.id) : [],
        assetids: hasAssets ? this.assets.map((i) => i.id) : [],
        excludeAssets: this.hasSelectedAssets ? false : this.excludeAssets,
      };

      const authStore = useAuthStore();
      authStore.getCurrentSession()
        .then(() => {
          this.dialog = false;
          const commonStore = useCommonStore();
          return commonStore.downloadThings(payload);
        });
    }
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
    noThingsSelected() {
      return (
        (!this.nodes || this.nodes.length === 0) &&
        (!this.pods || this.pods.length === 0) &&
        (!this.assets || this.assets.length === 0)
      );
    },
    hasSelectedAssets() {
      return this.assets && this.assets.length > 0;
    }
  }
};
</script>

<style>

</style>
